import React from "react";
import {
  Box,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import type { ColorCodingMetricType } from "@models/ColorCoding";
import { useColorCoding } from "@hooks/use-color-coding";

interface ColorCodingSectionProps {
  metricType: ColorCodingMetricType | null;
  selectedTemplateId: string | null | undefined;
  onTemplateChange: (templateId: string) => void;
}

const MISMATCH_MESSAGE =
  "The previously selected color coding template is not compatible with the current metric type and has been cleared.";

/**
 * Shared "Color Coding" config section for Table and Crosstab drawers.
 * A single Template dropdown (no enable toggle) filtered by metric type;
 * "None" turns color coding off. Handles the metric-type mismatch reset.
 *
 * Uses a plain MUI Select (like Time Period) rather than a searchable
 * autocomplete — there are only a couple of options.
 */
const ColorCodingSection: React.FC<ColorCodingSectionProps> = ({
  metricType,
  selectedTemplateId,
  onTemplateChange,
}) => {
  const {
    selectedTemplateId: value,
    templateOptions,
    showMismatchMessage,
    handleTemplateChange,
  } = useColorCoding({
    templateId: selectedTemplateId,
    metricType,
    onTemplateChange,
  });

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Color Coding
      </Typography>

      <FormControl fullWidth size="small">
        <FormLabel className="text-[12px] text-[#262626]">Template</FormLabel>
        <Select
          value={value}
          onChange={(e) => handleTemplateChange(e.target.value)}
        >
          {templateOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {showMismatchMessage && (
        <Box
          sx={{
            mt: 1.5,
            p: 1,
            px: 1.5,
            backgroundColor: "#FFF7E6",
            borderRadius: 1,
            border: "1px solid #FFD591",
          }}
        >
          <Typography variant="caption" sx={{ color: "#874D00" }}>
            {MISMATCH_MESSAGE}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ColorCodingSection;
